import React, { useMemo, useEffect, useState } from "react";
import { mapPalette, fmesh } from "../../core/globalVariables.js";

const DynamicStringMetricKey = ({
  globalState,
  globalRefs,
  longstandingGeoJSONRef,
  polygonCustomLayerRef
}) => {

  const selectedKey = globalState.selectedKey;
  const selectedYearEditing = globalState.selectedYearEditing;
  const keySummary = globalState.keySummary;
  const isRighdivrawerVisible = globalState.isRighdivrawerVisible;
  // ✅ added (expects these to exist in globalState)
  const multiGroupSelectedArray = globalState.multiGroupSelectedArray || [];
  const multuGroupSelectedArray = globalState.multuGroupSelectedArray;
  const setFeatureList = globalState.setFeatureList;
  const setMultuGroupSelectedArray = globalState.setMultuGroupSelectedArray;
  const setActiveFeatureId = globalState.setActiveFeatureId;
  const setIsRighdivrawerVisible = globalState.setIsRighdivrawerVisible;
  const setActiveTab = globalState.setActiveTab;
  const setIsRealmLeftClickedInfoVisible = globalState.setIsRealmLeftClickedInfoVisible;
  const mapMetaData = globalState.mapMetaData;

  const selectedKeyName = useMemo(() => {
    if (!selectedKey || !mapMetaData) return selectedKey;
    const metricMetadata =
      mapMetaData?.define_feature_data_simple_timeseries?.[selectedKey];
    return metricMetadata?.name || selectedKey;
  }, [selectedKey, mapMetaData]);

  // ✅ local checkbox state
  const checkedCategories = useMemo(() => {
    const set = new Set();

    if (!Array.isArray(multuGroupSelectedArray)) return set;

    longstandingGeoJSONRef.current?.features?.forEach((feature) => {
      const id = feature.skylark_id;

      if (multuGroupSelectedArray.includes(id)) {
        const value =
          feature?.feature_data_simple_timeseries?.[selectedKey]?.[selectedYearEditing];

        if (value != null) {
          set.add(value);
        }
      }
    });

    return set;
  }, [
    multuGroupSelectedArray,
    selectedKey,
    selectedYearEditing,
  ]);

  const stringToHexColor = (str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    return mapPalette[
      Math.abs(hash) % mapPalette.length
    ];
  };

  const selectedYearKey = useMemo(() => {
    if (!selectedKey) return null;
    const metricSummary = keySummary?.[selectedKey];
    if (!metricSummary) return null;
    const selectedYear =
      new Date(selectedYearEditing).getFullYear();
    return Object.keys(metricSummary).find(
      (timestamp) =>
        new Date(timestamp).getFullYear() === selectedYear
    );
  }, [
    selectedKey,
    selectedYearEditing,
    keySummary,
  ]);

  const categories = useMemo(() => {
    if (
      !selectedKey ||
      !selectedYearEditing ||
      !longstandingGeoJSONRef?.current?.features
    ) {
      return [];
    }

    const counts = {};

    longstandingGeoJSONRef.current.features.forEach((feature) => {
      const value =
        feature?.feature_data_simple_timeseries?.[selectedKey]?.[
        selectedYearEditing
        ];

      if (value == null) return;

      // Ensure category exists even when nothing selected
      if (!counts[value]) {
        counts[value] = 0;
      }

      // Count only selected features
      if ((multuGroupSelectedArray || []).includes(feature.skylark_id)) {
        counts[value] += 1;
      }
    });

    return Object.entries(counts);
  }, [
    selectedKey,
    selectedYearEditing,
    multuGroupSelectedArray,
  ]);

  if (!categories.length) {
    return null;
  }

  const hoverCategory = (hoveredValue) => {
    const featureLookup = new Map(
      longstandingGeoJSONRef.current.features.map(feature => [
        String(feature.skylark_id),
        feature,
      ])
    );

    fmesh.traverse((child) => {
      if (!child.isMesh || !child.material) return;
      const feature = featureLookup.get(String(child.name));
      if (!feature) {
        child.material.transparent = true;
        child.material.opacity = 0;
        return;
      }
      const featureValue =
        feature?.feature_data_simple_timeseries?.[selectedKey]?.[selectedYearEditing];
      child.material.transparent = true;
      child.material.opacity =
        String(featureValue) === String(hoveredValue) ? 1 : 0;
      child.material.needsUpdate = true;
    });

    polygonCustomLayerRef.current?.map_repaint();
  };

  const resetHover = () => {
    fmesh.traverse((child) => {
      if (!child.isMesh || !child.material) return;
      child.material.transparent = true;
      child.material.opacity = 1;
      child.material.needsUpdate = true;
    });
    polygonCustomLayerRef.current?.map_repaint();
  };

  // ✅ NEW: get all features matching a category
  const getFeaturesForCategory = (category) => {
    return longstandingGeoJSONRef.current.features.filter((feature) => {
      const value =
        feature?.feature_data_simple_timeseries?.[selectedKey]?.[selectedYearEditing];
      return String(value) === String(category);
    });
  };

  const toggleCategory = (category) => {
    const newSet = new Set(checkedCategories);
    const matchingFeatures = getFeaturesForCategory(category);
    const matchingIds = matchingFeatures.map(f => f.skylark_id);

    let nextSelectedArray;

    if (newSet.has(category)) {
      // TOGGLE OFF
      newSet.delete(category);

      nextSelectedArray = (multuGroupSelectedArray || []).filter(
        id => !matchingIds.includes(id)
      );

      setMultuGroupSelectedArray(nextSelectedArray);

    } else {
      // TOGGLE ON
      newSet.add(category);

      const existing = new Set(multuGroupSelectedArray || []);
      const additions = matchingIds.filter(id => !existing.has(id));

      nextSelectedArray = [...(multuGroupSelectedArray || []), ...additions];

      setMultuGroupSelectedArray(nextSelectedArray);
    }

    // setCheckedCategories(newSet);

    // ✅ IF NOTHING LEFT SELECTED → RUN CLOSE LOGIC
    if (!nextSelectedArray || nextSelectedArray.length === 0) {
      if (Array.isArray(multuGroupSelectedArray)) {
        multuGroupSelectedArray.forEach(skylarkId => {
          polygonCustomLayerRef.current
            ?.removedSkylarkIdFromsetMultuGroupSelectedArrayPoints(skylarkId);
        });
      }

      setMultuGroupSelectedArray([]);
      setIsRealmLeftClickedInfoVisible(false);
      setFeatureList([]);
      setActiveFeatureId(null);
      // setIsRighdivrawerVisible(false);
      setActiveTab(null);

      return;
    }

    // normal update UI focus
    const firstFeatureId = nextSelectedArray[0];
    const firstFeature = longstandingGeoJSONRef.current.features.find(
      f => f.skylark_id === firstFeatureId
    );

    if (firstFeature) {
      setActiveFeatureId(firstFeature.skylark_id);
      setIsRealmLeftClickedInfoVisible(true);
      setActiveTab("GROUP");
      setIsRighdivrawerVisible(true);
    }
  };

  const selectAllCategories = () => {
    const allIds = longstandingGeoJSONRef.current.features.map(
      feature => feature.skylark_id
    );
    setMultuGroupSelectedArray(allIds);
    if (allIds.length) {
      const firstFeature = longstandingGeoJSONRef.current.features.find(
        f => f.skylark_id === allIds[0]
      );
      if (firstFeature) {
        setActiveFeatureId(firstFeature.skylark_id);
        setIsRealmLeftClickedInfoVisible(true);
        setActiveTab("GROUP");
        setIsRighdivrawerVisible(true);
      }
    }
  };

  const deselectAllCategories = () => {
    (multuGroupSelectedArray || []).forEach(skylarkId => {
      polygonCustomLayerRef.current
        ?.removedSkylarkIdFromsetMultuGroupSelectedArrayPoints(skylarkId);
    });
    setMultuGroupSelectedArray([]);
    setFeatureList([]);
    setActiveFeatureId(null);
    setIsRealmLeftClickedInfoVisible(false);
    setActiveTab(null);
  };

  return (
    <div
      style={{
        position: 'absolute',
        width: '180px',
        zIndex: '1',
        right: isRighdivrawerVisible ? '390px' : '15px',
        top: '120px',
        height: '200px',
        maxHeight: '200px',
        overflowX: 'hidden',
        overflowY: 'hidden',
        paddingTop: "2px",
        paddingLeft: "2px",
        paddingRight: "2px",
        paddingBottom: "50px",
      }}
    >
      <div
        style={{
          border: "1px solid #ddd",
          borderRadius: "4px",
          paddingTop: "8px",
          paddingLeft: "8px",
          paddingRight: "8px",
          paddingBottom: "8px",
          marginTop: "10px",
          background: "#fff",
          fontSize: "10px",
          overflowX: 'hidden'
        }}
      >
        <div
          style={{
            fontWeight: "bold",
            marginBottom: "8px",
            color: "#29645D",
          }}
        >
          <span style={{ fontWeight: "bold", display: "inline-block" }}>
            {selectedKeyName}
          </span>

          <div
            style={{
              display: "flex",
              gap: "6px",
              marginBottom: "8px",
              marginTop: "8px",
            }}
          >
            <button
              onClick={selectAllCategories}
              style={{
                flex: 1,
                fontSize: "10px",
                padding: "3px 6px",
                cursor: "pointer",
                borderRadius: '3px',
                backgroundColor: 'rgb(222, 239, 231)',
                border: '1px solid rgb(41, 100, 93)'
              }}
            >
              Select All
            </button>

            <button
              onClick={deselectAllCategories}
              style={{
                flex: 1,
                fontSize: "10px",
                padding: "3px 6px",
                cursor: "pointer",
                borderRadius: '3px',
                backgroundColor: 'rgb(222, 239, 231)',
                border: '1px solid rgb(41, 100, 93)'
              }}
            >
              Deselect All
            </button>
          </div>

        </div>

        <div style={{
          overflowY: 'scroll',
          height: 'auto',
          maxHeight: '150px',
          overflowX: 'hidden'
        }}>

          {[...categories]
            .sort(([a], [b]) =>
              String(a).localeCompare(
                String(b),
                undefined,
                {
                  numeric: true,
                  sensitivity: "base"
                }
              )
            )
            .map(([category, count]) => (
              <div
                key={category}
                onClick={() => toggleCategory(category)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: "4px",
                  cursor: "pointer",
                  width: "100%",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                    marginBottom: "4px",
                    cursor: "pointer",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      minWidth: 0,
                      flex: "1 1 auto",
                      justifyContent: "flex-start",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checkedCategories.has(category)}
                      onChange={() => toggleCategory(category)}
                      onClick={(e) => e.stopPropagation()}
                    />

                    <div
                      style={{
                        width: "12px",
                        height: "12px",
                        borderRadius: "2px",
                        backgroundColor: stringToHexColor(category),
                        flexShrink: 0,
                      }}
                    />

                    <span
                      title={category || "Empty"}
                      style={{
                        textAlign: "left",
                        flex: "1 1 auto",
                        minWidth: 0,
                        cursor: "pointer",
                      }}
                    >
                      {category === "" || category == null ? (
                        <em>Empty</em>
                      ) : String(category).length > 18 ? (
                        `${String(category).substring(0, 18)}...`
                      ) : (
                        category
                      )}
                    </span>
                  </div>

                  <span
                    style={{
                      fontWeight: "bold",
                      marginLeft: "0px",
                      paddingRight: '15px',
                      color: "#29645D",
                      flexShrink: 0,
                      textAlign: "right",
                    }}
                  >
                    {count}
                  </span>
                </div>
              </div>
            ))
          }
        </div>

      </div>
    </div>
  );
};

export default DynamicStringMetricKey;